import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useParams, useLocation } from 'react-router-dom';
import { getOrderDetails, requestOrderReturn, verifyCheckout } from '../services/api';
import { useCart } from '../context/CartContext';
import CloudflareImage from '../components/ui/cloudflare-image';
import { toast } from 'react-hot-toast';

const NavbarSpacer = styled.div`
  height: 120px;
  @media (min-width: 768px) {
    height: 140px;
  }
  @media (min-width: 1024px) {
    height: 160px;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #fff;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 40px 16px;
  
  @media (min-width: 768px) {
    padding: 40px 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 40px 24px;
  }
  
  @media (min-width: 1536px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const BackLink = styled(Link)`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #000;
  text-decoration: none;
  
  svg {
    margin-right: 8px;
    width: 16px;
    height: 16px;
  }
`;

const Title = styled.h1`
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 1px;
  margin: 0;
  text-transform: uppercase;
`;

const OrderDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const OrderInfoSection = styled.div`
  flex: 2;
`;

const OrderSummarySection = styled.div`
  flex: 1;
  
  @media (min-width: 768px) {
    position: sticky;
    top: 180px;
  }
`;

const Card = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 24px;
`;

const CardHeader = styled.div`
  background-color: #f8f8f8;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e5e5;
`;

const CardTitle = styled.h2`
  font-size: 14px;
  font-weight: 500;
  margin: 0;
`;

const CardContent = styled.div`
  padding: 16px 24px;
`;

const OrderStatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 30px 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    background-color: #e0e0e0;
    transform: translateY(-50%);
    z-index: 1;
  }
`;

const StatusStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
`;

const StatusDot = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${props => (props.active ? (props.isNegative ? '#dc2626' : '#28a745') : '#e0e0e0')};
  margin-bottom: 8px;
`;

const StatusText = styled.div`
  font-size: 12px;
  color: ${props => (props.active ? (props.isNegative ? '#dc2626' : '#28a745') : '#666')};
  font-weight: ${props => props.active ? '500' : '400'};
`;

const OrderItemsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const OrderItem = styled.div`
  display: flex;
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const ItemDetails = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemImage = styled.div`
  width: 80px;
  height: 80px;
  margin-right: 16px;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 4px;
  }
`;

const ItemName = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const ItemVariant = styled.div`
  font-size: 12px;
  color: #666;
`;

const ItemPrice = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-top: 8px;
`;

const VerifyingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #000;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const VerifyingText = styled.div`
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const AddressInfo = styled.div`
  font-size: 14px;
  line-height: 1.6;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.div`
  font-size: 14px;
  color: #666;
`;

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  text-align: right;
`;

const PriceBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  
  &.total {
    font-weight: 600;
    margin-top: 8px;
    padding-top: 12px;
    border-top: 1px solid #e5e5e5;
  }
`;

const CoinIcon = styled(CloudflareImage)`
  width: 16px;
  height: 16px;
  margin-left: 6px;
`;

const TrackingCard = styled(Card)`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  margin-top: 20px;
`;

const TrackingLink = styled.a`
  color: #000;
  text-decoration: underline;
  font-weight: 500;
  
  &:hover {
    opacity: 0.7;
  }
`;

const ReturnButton = styled.button`
  background: #000;
  color: #fff;
  border: none;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 16px;
  width: 100%;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: opacity 0.2s;
  
  &:hover:not(:disabled) {
    opacity: 0.8;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ReturnText = styled.p`
  font-size: 12px;
  color: #666;
  margin-top: 8px;
  text-align: center;
`;

const ReturnForm = styled.form`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e5e5;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  background: #fff;
  color: #000;
  
  &:focus {
    outline: none;
    border-color: #000;
  }
`;

const CancelButton = styled.button`
  background: transparent;
  color: #666;
  border: none;
  font-size: 12px;
  cursor: pointer;
  margin-top: 8px;
  text-decoration: underline;
  width: 100%;
`;

const OrderDetailsPage = () => {
  const { orderNumber } = useParams();
  const location = useLocation();
  const { refreshCart } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [submittingReturn, setSubmittingReturn] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const hasTriggeredRef = React.useRef(false);

  useEffect(() => {
    const handlePaymentVerification = async () => {
      const searchParams = new URLSearchParams(location.search);
      const success = searchParams.get('success');
      const sessionId = searchParams.get('session_id');

      if (success === 'true' && sessionId && !hasTriggeredRef.current) {
        try {
          hasTriggeredRef.current = true; // Mark as triggered immediately
          setVerifying(true);
          // Fetch order details first to get the order ID
          const response = await getOrderDetails(orderNumber);
          if (response.success && response.order) {
            // Only verify if not already paid
            if (response.order.isPaid) {
              setOrder(response.order);
              refreshCart();
              return;
            }

            const verificationResponse = await verifyCheckout(sessionId, response.order._id);
            if (verificationResponse.success) {
              toast.success('Payment verified successfully!');
              // Refresh order data with confirmed status
              setOrder(verificationResponse.order);
              // Clear local cart
              refreshCart();
            }
          }
        } catch (err) {
          console.error('Verification error:', err);
          toast.error('Failed to verify payment. Please contact support.');
          hasTriggeredRef.current = false; // Allow retry on error if needed
        } finally {
          setVerifying(false);
        }
      }
    };

    handlePaymentVerification();
  }, [location.search, orderNumber, refreshCart]);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const response = await getOrderDetails(orderNumber);
        if (response.success) {
          setOrder(response.order);
        } else {
          setError(response.error);
        }
      } catch (err) {
        setError('Failed to fetch order details.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderNumber]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!order) {
    return <div>Order not found.</div>;
  }

  const isReturnable = () => {
    if (order.orderStatus !== 'Delivered') return false;
    const deliveredDate = new Date(order.deliveredAt || order.updatedAt);
    const diffTime = Math.abs(new Date() - deliveredDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  const handleReturnSubmit = async (e) => {
    e.preventDefault();
    if (!returnReason.trim()) {
      toast.error('Please provide a reason for the return');
      return;
    }

    try {
      setSubmittingReturn(true);
      const response = await requestOrderReturn(order.orderNumber, returnReason);
      if (response.success) {
        setOrder({ ...order, orderStatus: 'Return Requested', returnReason });
        setShowReturnForm(false);
        toast.success('Return request submitted successfully');
      } else {
        toast.error(response.error || 'Failed to submit return request.');
      }
    } catch (err) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setSubmittingReturn(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const getOrderStatuses = () => {
    const negativeStatuses = ['Cancelled', 'Refunded', 'Returned'];
    if (negativeStatuses.includes(order.orderStatus)) {
      return [order.orderStatus];
    }
    if (order.orderStatus === 'Return Requested') {
      return ['Processing', 'Shipped', 'Delivered', 'Return Requested'];
    }
    return ['Processing', 'Shipped', 'Delivered'];
  };

  const orderStatuses = getOrderStatuses();
  const currentStatusIndex = orderStatuses.indexOf(order.orderStatus);
  const isNegativeStatus = ['Cancelled', 'Refunded', 'Returned', 'Return Requested'].includes(order.orderStatus);

  return (
    <PageContainer>
      <NavbarSpacer />
      {verifying && (
        <VerifyingOverlay>
          <Spinner />
          <VerifyingText>Verifying your payment...</VerifyingText>
        </VerifyingOverlay>
      )}
      <ContentWrapper>
        <PageHeader>
          <BackLink to="/orders">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Orders
          </BackLink>
          <Title>Order Details</Title>
        </PageHeader>

        <OrderDetailsContainer>
          <OrderInfoSection>
            <Card>
              <CardHeader>
                <CardTitle>Order #{order.orderNumber}</CardTitle>
              </CardHeader>
              <CardContent>
                <InfoRow>
                  <InfoLabel>Order Date</InfoLabel>
                  <InfoValue>{formatDate(order.createdAt)}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Status</InfoLabel>
                  <InfoValue style={{ color: isNegativeStatus ? '#dc2626' : '#28a745' }}>{order.orderStatus}</InfoValue>
                </InfoRow>
                {order.orderStatus === 'Delivered' && order.deliveredAt && (
                  <InfoRow>
                    <InfoLabel>Delivered On</InfoLabel>
                    <InfoValue>{formatDate(order.deliveredAt)}</InfoValue>
                  </InfoRow>
                )}
                {order.orderStatus === 'Returned' && order.returnedAt && (
                  <InfoRow>
                    <InfoLabel>Returned On</InfoLabel>
                    <InfoValue>{formatDate(order.returnedAt)}</InfoValue>
                  </InfoRow>
                )}
                {order.orderStatus === 'Return Requested' && order.returnRequestedAt && (
                  <InfoRow>
                    <InfoLabel>Requested On</InfoLabel>
                    <InfoValue>{formatDate(order.returnRequestedAt)}</InfoValue>
                  </InfoRow>
                )}
                {order.orderStatus === 'Return Requested' && order.returnReason && (
                  <InfoRow>
                    <InfoLabel>Return Reason</InfoLabel>
                    <InfoValue>{order.returnReason}</InfoValue>
                  </InfoRow>
                )}
                {order.orderStatus === 'Cancelled' && order.cancelledAt && (
                  <InfoRow>
                    <InfoLabel>Cancelled On</InfoLabel>
                    <InfoValue>{formatDate(order.cancelledAt)}</InfoValue>
                  </InfoRow>
                )}
                {order.orderStatus === 'Refunded' && order.refundedAt && (
                  <InfoRow>
                    <InfoLabel>Refunded On</InfoLabel>
                    <InfoValue>{formatDate(order.refundedAt)}</InfoValue>
                  </InfoRow>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Order Status</CardTitle>
              </CardHeader>
              <CardContent>
                <OrderStatusBar>
                  {orderStatuses.map((status, index) => (
                    <StatusStep key={status}>
                      <StatusDot active={index <= currentStatusIndex} isNegative={isNegativeStatus} />
                      <StatusText active={index <= currentStatusIndex} isNegative={isNegativeStatus}>{status}</StatusText>
                    </StatusStep>
                  ))}
                </OrderStatusBar>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Items ({order.orderItems?.length || 0})</CardTitle>
              </CardHeader>
              <CardContent>
                <OrderItemsList>
                  {order.orderItems?.map(item => (
                    <OrderItem key={item._id}>
                      <ItemImage>
                        <CloudflareImage src={item.image} alt={item.name} />
                      </ItemImage>
                      <ItemDetails>
                        <ItemName>{item.name}</ItemName>
                        {item.size !== 'OneSize' && <ItemVariant>Size: {item.size}</ItemVariant>}
                        <ItemVariant>Qty: {item.quantity}</ItemVariant>
                        <ItemPrice>{formatCurrency(item.price)}</ItemPrice>
                      </ItemDetails>
                    </OrderItem>
                  ))}
                </OrderItemsList>
              </CardContent>
            </Card>
          </OrderInfoSection>

          <OrderSummarySection>
            <Card>
              <CardHeader>
                <CardTitle>Shipping Address</CardTitle>
              </CardHeader>
              <CardContent>
                <AddressInfo>
                  {order.shippingAddress?.addressLine1 || 'N/A'}<br />
                  {order.shippingAddress?.addressLine2 && <>{order.shippingAddress.addressLine2}<br /></>}
                  {order.shippingAddress?.townCity}, {order.shippingAddress?.postcode}<br />
                </AddressInfo>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Payment Information</CardTitle>
              </CardHeader>
              <CardContent>
                <InfoRow>
                  <InfoLabel>Payment Method</InfoLabel>
                  <InfoValue>{order.paymentMethod}</InfoValue>
                </InfoRow>
              </CardContent>
            </Card>

            {order.trackingNumber && (
              <TrackingCard>
                <CardHeader>
                  <CardTitle>Tracking Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <InfoRow>
                    <InfoLabel>Carrier</InfoLabel>
                    <InfoValue>{order.carrier?.toUpperCase() || 'N/A'}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Tracking #</InfoLabel>
                    <InfoValue>
                      {order.trackingUrl ? (
                        <TrackingLink href={order.trackingUrl} target="_blank" rel="noopener noreferrer">
                          {order.trackingNumber}
                        </TrackingLink>
                      ) : (
                        order.trackingNumber
                      )}
                    </InfoValue>
                  </InfoRow>
                </CardContent>
              </TrackingCard>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Price Details</CardTitle>
              </CardHeader>
              <CardContent>
                <PriceBreakdown>
                  <PriceRow>
                    <span>Subtotal</span>
                    <span>{formatCurrency(order.orderItems?.reduce((acc, item) => acc + item.price * item.quantity, 0) || 0)}</span>
                  </PriceRow>
                  {order.couponDiscount > 0 && (
                    <PriceRow>
                      <span>Coupon Discount</span>
                      <span>- {formatCurrency(order.couponDiscount)}</span>
                    </PriceRow>
                  )}
                  {order.giftCardDiscount > 0 && (
                    <PriceRow>
                      <span>Gift Card Discount</span>
                      <span>- {formatCurrency(order.giftCardDiscount)}</span>
                    </PriceRow>
                  )}
                  {order.madCoinsDiscount > 0 && (
                    <PriceRow>
                      <span>MAD Coins Discount</span>
                      <span>- {formatCurrency(order.madCoinsDiscount)}</span>
                    </PriceRow>
                  )}
                  <PriceRow>
                    <span>Shipping</span>
                    <span>{formatCurrency(order.shippingPrice)}</span>
                  </PriceRow>
                  <PriceRow>
                    <span>Tax</span>
                    <span>{formatCurrency(order.taxPrice)}</span>
                  </PriceRow>
                  <PriceRow className="total">
                    <span>Total</span>
                    <span>{formatCurrency(order.totalPrice)}</span>
                  </PriceRow>
                  {order.coinTransaction && order.coinTransaction.amount > 0 && (
                    <PriceRow>
                      <span>MadCoins Earned</span>
                      <span style={{ display: 'flex', alignItems: 'center', color: order.orderStatus === 'Return Requested' ? '#666' : 'inherit', textDecoration: order.orderStatus === 'Return Requested' ? 'line-through' : 'none' }}>
                        {order.coinTransaction?.amount?.toFixed(2) || '0.00'}
                        <CoinIcon src="/images/madicon.png" alt="MAD Coin" />
                      </span>
                    </PriceRow>
                  )}
                  {order.orderStatus === 'Return Requested' && order.coinTransaction && (
                    <ReturnText style={{ color: '#dc2626', fontWeight: 500, margin: '0', textAlign: 'right' }}>
                      (Coins deducted on return request)
                    </ReturnText>
                  )}
                </PriceBreakdown>

                {isReturnable() && !showReturnForm && (
                  <>
                    <ReturnButton onClick={() => setShowReturnForm(true)}>Return Order</ReturnButton>
                    <ReturnText>Orders can be returned within 7 days of delivery.</ReturnText>
                  </>
                )}

                {showReturnForm && (
                  <ReturnForm onSubmit={handleReturnSubmit}>
                    <FormGroup>
                      <Label>Reason for Return</Label>
                      <TextArea
                        placeholder="Please tell us why you are returning this order..."
                        value={returnReason}
                        onChange={(e) => setReturnReason(e.target.value)}
                        required
                      />
                    </FormGroup>
                    <ReturnButton type="submit" disabled={submittingReturn}>
                      {submittingReturn ? 'Submitting...' : 'Submit Return'}
                    </ReturnButton>
                    <CancelButton type="button" onClick={() => setShowReturnForm(false)}>
                      Cancel
                    </CancelButton>
                  </ReturnForm>
                )}
              </CardContent>
            </Card>
          </OrderSummarySection>
        </OrderDetailsContainer>
      </ContentWrapper>
    </PageContainer>
  );
};

export default OrderDetailsPage;
